import { useCallback, useState } from "react";
import { Icon } from "../Icon";
import { PopupCard } from "../PopupCard";
import styles from "./RichTextEditor.module.scss";
import { useCurrentEditor } from "@tiptap/react";
import { ToolbarButton } from "./ToolbarButton";
import { DropdownMenu } from "../DropdownMenu/DropdownMenu";
import { motion } from "framer-motion";

type RichTextEditorToolbarProps = {
  editorId: string;
  infoMessage?: string;
  textVariables?: string[];
  cursorPosition?: number | undefined;
  placeholderNode?: React.ReactNode;
};

export function RichTextEditorToolbar({
  editorId,
  infoMessage,
  textVariables,
  cursorPosition,
  placeholderNode,
}: RichTextEditorToolbarProps) {
  const { editor } = useCurrentEditor();
  const [infoOpen, setInfoOpen] = useState(false);

  const setLink = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const previousUrl = editor?.getAttributes("link").href;
      const url = window.prompt("URL", previousUrl);

      if (url === null) {
        return;
      }

      if (url === "") {
        editor?.chain().focus().extendMarkRange("link").unsetLink().run();

        return;
      }

      editor
        ?.chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url })
        .run();
    },
    [editor],
  );

  const handleInsertContent = (variable: string) => {
    if (cursorPosition !== undefined) {
      editor
        ?.chain()
        .focus()
        .setTextSelection({ from: cursorPosition, to: cursorPosition })
        .run();
      editor?.commands.insertContent(variable);
    }
  };

  if (!editor) {
    return null;
  }

  const buttonGroupOne = [
    {
      onClick: () => editor.chain().focus().toggleBold().run(),
      disabled: !editor.can().chain().focus().toggleBold().run(),
      iconName: "format_bold",
      className: "bold",
    },
    {
      onClick: () => editor.chain().focus().toggleItalic().run(),
      disabled: !editor.can().chain().focus().toggleItalic().run(),
      iconName: "format_italic",
      className: "italic",
    },
  ];

  const buttonGroupTwo = [
    {
      onClick: () => editor.chain().focus().toggleBulletList().run(),
      iconName: "format_list_bulleted",
      className: "bulletList",
    },
    {
      onClick: () => editor.chain().focus().toggleOrderedList().run(),
      iconName: "format_list_numbered",
      className: "orderedList",
    },
  ];

  return (
    <>
      <div className={styles.toolbar}>
        {!!placeholderNode && (
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 30 }}
            className={styles.placeholderNode}
          >
            {placeholderNode}
          </motion.div>
        )}

        {buttonGroupOne.map((button) => (
          <ToolbarButton
            key={button.iconName}
            onClick={button.onClick}
            disabled={button.disabled}
            iconName={button.iconName}
            className={button.className}
          />
        ))}

        <div className={styles.verticalDivider}></div>

        {buttonGroupTwo.map((button) => (
          <ToolbarButton
            key={button.iconName}
            onClick={button.onClick}
            iconName={button.iconName}
            className={button.className}
          />
        ))}

        <div className={styles.verticalDivider}></div>

        {infoMessage && (
          <button type="button" onClick={() => setInfoOpen(true)}>
            <Icon name="info" />
          </button>
        )}

        <button
          type="button"
          onClick={setLink}
          className={editor.isActive("link") ? styles.active : ""}
        >
          <Icon name="add_link" />
        </button>

        {textVariables && (
          <DropdownMenu
            id={`${editorId}_DROPDOWN`}
            activator={<ToolbarButton iconName="more_vert" />}
            menuOptions={textVariables.map((variable) => ({
              label: variable.split("#")[1],
              onSelect: () => handleInsertContent(variable),
            }))}
          />
        )}
      </div>
      <PopupCard
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        children={<p>{infoMessage}</p>}
      />
    </>
  );
}
